import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import cx from "classnames";
import PropTypes from "prop-types";

import styles from "./TimeInput.module.scss";

const TimeInput = forwardRef((props, ref) => {
  const {
    suffix = "",
    className,
    inputClassName,
    value,
    onChange,
    onValueChange,
    onBlur,
    placeholder,
    disabled,
    min,
    max,
    ...rest
  } = props;

  const [displayValue, setDisplayValue] = useState(value?.toString() || "");
  const internalRef = useRef(null);
  const inputRef = ref || internalRef;

  const formatNumericValue = useCallback((inputValue) => {
    // Remove any non-digit characters except decimal point
    const numericValue = inputValue.replace(/[^\d.]/g, "");

    // Ensure only one decimal point
    const parts = numericValue.split(".");
    if (parts.length > 2) {
      return `${parts[0]}.${parts.slice(1).join("")}`;
    }

    return numericValue;
  }, []);

  const validateValue = useCallback(
    (numericValue) => {
      const floatValue = parseFloat(numericValue);

      if (Number.isNaN(floatValue)) {
        return "";
      }

      let validatedValue = floatValue;

      if (min !== undefined && validatedValue < min) {
        validatedValue = min;
      }

      if (max !== undefined && validatedValue > max) {
        validatedValue = max;
      }

      return validatedValue.toString();
    },
    [min, max]
  );

  const handleChange = useCallback(
    (e) => {
      const inputValue = e.target.value;
      const cursorPosition = e.target.selectionStart;

      // Remove suffix if present
      const valueWithoutSuffix = inputValue.replace(suffix, "");
      const formattedValue = formatNumericValue(valueWithoutSuffix);

      setDisplayValue(formattedValue);

      // Set cursor position after the numeric value (before suffix)
      setTimeout(() => {
        if (inputRef.current) {
          const numericLength = formattedValue.length;
          const maxCursorPos = numericLength;

          // If cursor was in suffix area, move it to end of numeric value
          if (cursorPosition > maxCursorPos) {
            inputRef.current.setSelectionRange(maxCursorPos, maxCursorPos);
          } else {
            // Keep cursor in same relative position within numeric value
            const newCursorPos = Math.min(cursorPosition, maxCursorPos);
            inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
          }
        }
      }, 0);

      // Call onChange if provided
      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: formattedValue,
          },
        };
        onChange(syntheticEvent);
      }

      // Call onValueChange if provided (similar to react-number-format's API)
      if (onValueChange) {
        const floatValue = parseFloat(formattedValue);
        onValueChange({
          formattedValue: formattedValue ? `${formattedValue}${suffix}` : "",
          value: formattedValue,
          floatValue: Number.isNaN(floatValue) ? undefined : floatValue,
        });
      }
    },
    [formatNumericValue, onChange, onValueChange, suffix, inputRef]
  );

  const handleKeyDown = useCallback(
    (e) => {
      const cursorPosition = e.target.selectionStart;
      const numericLength = displayValue.length;

      // If cursor is in suffix area and user tries to delete/backspace
      if (cursorPosition > numericLength && (e.key === "Backspace" || e.key === "Delete")) {
        e.preventDefault();
        // Move cursor to end of numeric value
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(numericLength, numericLength);
          }
        }, 0);
      }
    },
    [displayValue, inputRef]
  );

  const handleClick = useCallback(
    (e) => {
      const cursorPosition = e.target.selectionStart;
      const numericLength = displayValue.length;

      // If user clicks in suffix area, move cursor to end of numeric value
      if (cursorPosition > numericLength) {
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(numericLength, numericLength);
          }
        }, 0);
      }
    },
    [displayValue, inputRef]
  );

  const handleBlur = useCallback(
    (e) => {
      const inputValue = e.target.value;
      const valueWithoutSuffix = inputValue.replace(suffix, "");
      const formattedValue = formatNumericValue(valueWithoutSuffix);
      const validatedValue = validateValue(formattedValue);

      setDisplayValue(validatedValue);

      // Call onBlur if provided
      if (onBlur) {
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: validatedValue,
          },
        };
        onBlur(syntheticEvent);
      }

      // Call onValueChange with validated value
      if (onValueChange) {
        const floatValue = parseFloat(validatedValue);
        onValueChange({
          formattedValue: validatedValue ? `${validatedValue}${suffix}` : "",
          value: validatedValue,
          floatValue: Number.isNaN(floatValue) ? undefined : floatValue,
        });
      }
    },
    [formatNumericValue, validateValue, onBlur, onValueChange, suffix]
  );

  // Update display value when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setDisplayValue(value?.toString() || "");
    }
  }, [value]);

  const finalDisplayValue = displayValue ? `${displayValue}${suffix}` : "";

  return (
    <input
      {...rest}
      ref={inputRef}
      type="text"
      value={finalDisplayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={cx(styles.input, className, inputClassName)}
    />
  );
});

TimeInput.propTypes = {
  suffix: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onValueChange: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
};

export default TimeInput;
